import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import {
  type DeliveryFacets,
  deliveryFacetsQuery,
  deliveryGalleryQuery,
  type DeliveryGalleryItem,
  type DeliveryMediaFilter,
} from '@/services/deliveries';

/** The URL names. Short, because a visitor may well be pasting this to someone. */
const PLACE_PARAM = 'place';
const SHOW_PARAM = 'show';

const MEDIA_FILTERS: readonly DeliveryMediaFilter[] = ['all', 'photos', 'videos', 'testimonies'];

const isMediaFilter = (value: string | null): value is DeliveryMediaFilter =>
  value !== null && (MEDIA_FILTERS as readonly string[]).includes(value);

export type DeliveryGallery = {
  /** Null means every destination. */
  destination: string | null;
  media: DeliveryMediaFilter;
  setDestination: (destination: string | null) => void;
  setMedia: (media: DeliveryMediaFilter) => void;
  clearFilters: () => void;
  filtered: boolean;
  facets: DeliveryFacets | undefined;
  /** Every page loaded so far, flattened, in order. */
  items: DeliveryGalleryItem[];
  /** How many match the filters, loaded or not. */
  total: number;
  loading: boolean;
  error: boolean;
  hasMore: boolean;
  loadingMore: boolean;
  loadMore: () => void;
  /** Refetches after a failure. Distinct from clearing the filters. */
  retry: () => void;
};

/**
 * The gallery's state: what is being shown, and how more of it arrives.
 *
 * ## The filters live in the URL
 *
 * `?place=Kampala,+Uganda&show=videos` — so a filtered view can be sent to
 * somebody, bookmarked, and linked to from elsewhere on the site. That last
 * one matters more than it sounds: the proof strip on a vehicle page can point
 * at the clips, and the parent delivery page can point a Bunagana reader at
 * Bunagana, without either of them needing a page of its own.
 *
 * It is also why the filters are not component state. A chip row that forgets
 * itself on the back button is a chip row people stop trusting.
 *
 * `replace` on every change: twelve taps through the chips should not leave
 * twelve entries in the history for somebody to back out through.
 *
 * ## An unknown value is ignored, not an error
 *
 * A hand-edited or stale URL (`?show=drone`, or a destination Kabex no longer
 * delivers to) falls back to the unfiltered archive rather than rendering an
 * empty page. The destination is checked against the facets, so a link that
 * was valid last month and is not today still shows something.
 *
 * ## Pages accumulate
 *
 * "Load more" and not numbered pages, and not infinite scroll either: an
 * archive is browsed rather than searched, so page 3 means nothing — but a
 * feed that keeps loading as you scroll also means a footer nobody can reach
 * and a page a crawler never finishes. A button is the one option that is both
 * endless and finishable.
 */
export function useDeliveryGallery(): DeliveryGallery {
  const [params, setParams] = useSearchParams();
  const facetsResult = useQuery(deliveryFacetsQuery());
  const facets = facetsResult.data;

  const requestedPlace = params.get(PLACE_PARAM);
  const show = params.get(SHOW_PARAM);

  // Checked against what the archive actually holds. Until the facets land
  // nothing is rejected, so the first paint of a shared link is already
  // filtered rather than showing everything and then narrowing.
  const destination = useMemo(() => {
    if (!requestedPlace) return null;
    if (!facets) return requestedPlace;
    return facets.destinations.some((facet) => facet.value === requestedPlace)
      ? requestedPlace
      : null;
  }, [requestedPlace, facets]);

  const media: DeliveryMediaFilter = isMediaFilter(show) ? show : 'all';

  const setParam = useCallback(
    (key: string, value: string | null) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (value === null) next.delete(key);
          else next.set(key, value);
          return next;
        },
        { replace: true, preventScrollReset: true },
      );
    },
    [setParams],
  );

  const setDestination = useCallback(
    (value: string | null) => setParam(PLACE_PARAM, value),
    [setParam],
  );

  // 'all' is the absence of a filter, so it clears the parameter rather than
  // writing it — a URL that says `?show=all` is a URL that says nothing.
  const setMedia = useCallback(
    (value: DeliveryMediaFilter) => setParam(SHOW_PARAM, value === 'all' ? null : value),
    [setParam],
  );

  const clearFilters = useCallback(
    () => setParams(new URLSearchParams(), { replace: true, preventScrollReset: true }),
    [setParams],
  );

  const query = useInfiniteQuery(
    deliveryGalleryQuery({ ...(destination ? { destination } : {}), media }),
  );

  const items = useMemo(
    () => query.data?.pages.flatMap((page) => page.items) ?? [],
    [query.data],
  );

  return {
    destination,
    media,
    setDestination,
    setMedia,
    clearFilters,
    filtered: destination !== null || media !== 'all',
    facets,
    items,
    total: query.data?.pages[0]?.total ?? 0,
    loading: query.isPending,
    error: query.isError,
    hasMore: query.hasNextPage,
    loadingMore: query.isFetchingNextPage,
    loadMore: () => void query.fetchNextPage(),
    retry: () => void query.refetch(),
  };
}
